import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { LineProps, Point, ResponsiveLine, Serie, SliceTooltipProps } from "@nivo/line";
import { addYears, startOfToday } from "date-fns";
import { Fragment, useMemo } from "react";
import { DateOnly } from "src/utils/dates";

type LineChartProps = {
  dataset: LineProps["data"];
  colors?: Palette[];
  loadingColor?: Palette[];
  disabled?: boolean;
  tooltipValueFormat?: (points: Point[]) => string;
  emptyStateTooltipText?: string;
} & Omit<LineProps, "data" | "colors" | "isInteractive">;

export function LineChart(props: LineChartProps) {
  const { dataset, colors, loadingColor, disabled, tooltipValueFormat, emptyStateTooltipText, ...otherProps } = props;
  const tid = useTestIds({});
  const Chart = useMemo(
    () => (
      <ResponsiveLine
        data={dataset}
        colors={disabled ? loadingColor : colors}
        isInteractive={!disabled}
        sliceTooltip={tooltipValueFormat ? customTooltip(tooltipValueFormat) : undefined}
        enableGridX={false}
        enablePoints={false}
        theme={{ axis: { ticks: { text: { fontSize: 10, fill: Palette.Gray700 } } } }}
        {...otherProps}
      />
    ),
    [dataset, colors, loadingColor, disabled, tooltipValueFormat, otherProps],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

function customTooltip(tooltipValueFormat: (points: Point[]) => string) {
  return ({ slice }: SliceTooltipProps) => (
    <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(300).tal.$}>{tooltipValueFormat(slice.points)}</div>
  );
}

export function generateEmptyDataset(yMin: number, yMax: number): Serie[] {
  const today = new DateOnly(startOfToday());
  return [
    {
      id: "empty_dataset",
      data: [today, new DateOnly(addYears(today, -1))].map((x, i) => ({ x, y: i === 0 ? yMin : yMax })), // Use full range of x/y axis
    },
  ];
}
